import { useQuery } from "@tanstack/react-query";
import { getCurrentUser as getCurrentCognitoUser } from "aws-amplify/auth";
import { gqlReqNext } from "~utils/api/gql-req/next";

const query = /* GraphQL */ `
	query GetCurrentAccountSlug {
		getCurrentAccount {
			id
			slug
		}
	}
`;

interface GetCurrentAccountResult {
	id: string;
	slug: string;
}

export const getCurrentAccountSlugQueryKey = () => ["getCurrentAccountSlug"];

const getCurrentAccountSlugQueryFn = async (
	signal?: AbortSignal,
): Promise<GetCurrentAccountResult | null> => {
	// `getCurrentAccount` is a Cognito-user-pool-only field. Without a local
	// session, gqlReqNext would still fall through to a guest IAM POST that is
	// guaranteed to fail authorization. Check for a local session first (no
	// network round-trip - same call AuthProvider already makes) so a
	// signed-out visitor never waits on, or triggers, a doomed request.
	const hasLocalSession = await getCurrentCognitoUser()
		.then(() => true)
		.catch(() => false);

	if (!hasLocalSession) return null;

	try {
		// gqlReqNext unwraps the first top-level query field, so the runtime value
		// is the `{ id, slug }` object (or null for signed-out visitors). The
		// generic return type collapses the object's keys to a union, so we
		// re-narrow with a cast here to keep callers type-safe.
		const result = (await gqlReqNext<
			{ getCurrentAccount: GetCurrentAccountResult | null },
			Record<string, never>
		>({ query, signal })) as unknown as GetCurrentAccountResult | null;
		return result;
	} catch {
		// Fail closed: an expired/invalid session resolves to signed-out
		// instead of throwing into the UI.
		return null;
	}
};

/**
 * Landing-page-only query that resolves the minimum data needed to build
 * the "Go to portal" CTA (the account slug). Skips the 5+ sub-resolvers
 * that the full {@link useGetAuthAccount} fans out to, which makes it
 * dramatically cheaper on the cold-start landing path.
 *
 * Tagged as landing-only because the rest of the application (billing,
 * customisation, app-navigation-bar, etc.) still needs the full account
 * shape from {@link useGetAuthAccount}.
 */
export const useGetCurrentAccountSlug = () => {
	return useQuery({
		queryKey: getCurrentAccountSlugQueryKey(),
		queryFn: ({ signal }) => getCurrentAccountSlugQueryFn(signal),
		staleTime: 30_000,
	});
};
